import { Link, generatePath, useLocation } from "react-router-dom";

import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Pagination,
  Tooltip,
  IconButton,
} from "@mui/material";
import { VisibilityOutlined, LoopOutlined, RemoveCircleOutline } from "@mui/icons-material";

import nameof from "ts-nameof.macro";
import { dateToStringShort } from "../../../common/utils/dateTime";

import SelectExtended from "../../../common/components/mui-extended/SelectExtended";
import DatePickerExtended from "../../../common/components/mui-extended/DatePickerExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { PublicRoutesConfig } from "../../RoutesConfig";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import { Rules } from "../../../common/validationRules";
import { LoadingButton } from "@mui/lab";
import { PageDefaults } from "../../../common/interfaces/PageDto";
import AlertExtended from "../../../common/components/mui-extended/AlertExtended";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import { useSearchForm } from "../../../common/hooks/useSearchForm";
import TableSkeleton from "../../../common/components/TableSkeleton";
import { CountdownCircleTimer } from "react-countdown-circle-timer";

import {
  ObligationResponseSearchDto,
  ObligationResponseSearchResultDto,
  PersonTaxpayerType,
  ReportStatus,
  getTaxpayerPinLabel,
} from "../../../interfaces/Obligation";
import TaxpayerPinType from "./TaxpayerPinType";
import obligationService from "../../../services/obligationService";
import { deepOrange, green, grey } from "@mui/material/colors";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const defaultModel: ObligationResponseSearchDto = {
  ...PageDefaults,
  taxpayerPinType: PersonTaxpayerType.Egn,
};
export default function Search() {
  const searchButtonId = "searchButtonId";
  const loading = useAxiosLoader(false);
  const location = useLocation();
  const [hasInProgressObligations, setHasInProgressObligation] = useState(false);
  const [previousInProgressObligations, setPreviousInProgressObligations] = useState<Array<number>>([]);

  const form = useSearchForm<ObligationResponseSearchDto, ObligationResponseSearchResultDto>({
    defaultModel: defaultModel,
    rootSearchPath: PublicRoutesConfig.NapObligations.Search.Path,
    searchAsync: async (query: string) => {
      await obligationService.getResponsesAsync(query).then(async ({ data }) => {
        await obligationService.getResponsesCountAsync(query).then(({ data: count }) => {
          form.setResults(data, count);
          updateInProgressObligations(data);
        });
      });
    },
  });

  const updateInProgressObligations = (obligations: Array<ObligationResponseSearchResultDto>) => {
    previousInProgressObligations.forEach((id) => {
      const ob = obligations.find((e) => e.obligationResponseId === id);
      if (ob && ob.reportStatus !== ReportStatus.PROCESSING) {
        toast.info(
          `Има промяна на статус на заявена проверка с входящ номер ${ob.entryNumber} / ${dateToStringShort(
            ob.requestDate
          )}.`
        );
      }
    });

    let inProgressObligations = new Array<number>();
    obligations.forEach((ob) => {
      if (ob.reportStatus === ReportStatus.PROCESSING) {
        inProgressObligations.push(ob.obligationResponseId);
      }
    });

    setPreviousInProgressObligations(inProgressObligations);

    setHasInProgressObligation(inProgressObligations.length > 0);
  };

  useEffect(() => {
    const state = { ...location.state };
    if (state["autosearch"]) {
      const isRedirectRefresh = state["autosearch"] === true;
      if (isRedirectRefresh) document.getElementById(searchButtonId)?.click();
    }
  }, []);

  const renderForm = () => {
    return (
      <form onReset={form.onReset} onSubmit={form.onSubmit}>
        <Stack spacing={3}>
          <SectionExtended title="Данни на задължено лице">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TaxpayerPinType name={nameof(defaultModel.taxpayerPinType)} form={form} />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextFieldExtended
                  label={getTaxpayerPinLabel(form.watch("taxpayerPinType"))}
                  name={nameof(defaultModel.taxpayerPin)}
                  form={form}
                  onInput={(e: any) => {
                    e.target.value = e.target.value.toString().slice(0, 13);
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <DatePickerExtended
                  label="Дата"
                  name={nameof(defaultModel.requestDate)}
                  form={form}
                  rules={{ ...Rules.Date }}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <TextFieldExtended label="Вх. номер" name={nameof(defaultModel.entryNumber)} form={form} />
              </Grid>

              <Grid item xs={12} md={12} lg={8}>
                <SelectExtended
                  label="Нотариално производство"
                  name={nameof(defaultModel.documentTypeId)}
                  form={form}
                  searchRequest={obligationService.getDocumentTypes}
                  initRequest={obligationService.getDocumentTypesByIds}
                />
              </Grid>
            </Grid>
          </SectionExtended>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Button
                startIcon={<RemoveCircleOutline />}
                type="reset"
                onClick={() => setHasInProgressObligation(false)}
              >
                Изчисти
              </Button>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1}>
                {hasInProgressObligations && (
                  <CountdownCircleTimer
                    size={37}
                    strokeWidth={2}
                    isPlaying
                    duration={60}
                    colors={"#dc3c00"}
                    onComplete={() => {
                      setHasInProgressObligation(false);
                      document.getElementById(searchButtonId)?.click();
                    }}
                  >
                    {({ remainingTime }) => (
                      <Tooltip title={`Автоматична проверка след ${remainingTime} секунди`} arrow placement="left">
                        <IconButton sx={{ fontSize: 16 }} color="secondary">
                          {remainingTime}
                        </IconButton>
                      </Tooltip>
                    )}
                  </CountdownCircleTimer>
                )}
                <LoadingButton
                  id={searchButtonId}
                  variant="contained"
                  loading={loading}
                  loadingPosition="start"
                  startIcon={PublicRoutesConfig.NapObligations.Search.Icon}
                  type="submit"
                >
                  Търси
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </form>
    );
  };

  const renderTable = () => {
    return loading ? (
      <TableSkeleton />
    ) : form.results && form.results.length > 0 ? (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Вх. номер</TableCell>
                <TableCell>Идентификатор</TableCell>
                <TableCell>Нотариално производство</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell width="90px"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {form.results.map((item) => (
                <TableRow
                  key={item.obligationResponseId}
                  hover
                  sx={{
                    backgroundColor:
                      item.reportStatus === ReportStatus.PROCESSING
                        ? grey[50]
                        : item.reportStatus === ReportStatus.SUCCESS_NO_OBLIGATIONS
                        ? green[50]
                        : deepOrange[50],
                  }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{dateToStringShort(item.requestDate)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{item.entryNumber}</TableCell>
                  <TableCell>
                    {item.taxpayerPinType}: {item.taxpayerPin}
                  </TableCell>
                  <TableCell>{item.documentType}</TableCell>
                  <TableCell>{item.reportStatusNomValue}</TableCell>
                  <TableCell>
                    <Stack direction="row" justifyContent="end">
                      <Tooltip title={PublicRoutesConfig.NapObligations.Preview.Label}>
                        <IconButton
                          component={Link}
                          to={generatePath(PublicRoutesConfig.NapObligations.Preview.Path, {
                            id: item.obligationResponseId.toString(),
                          })}
                          disabled={item.reportStatus === ReportStatus.PROCESSING}
                        >
                          {item.reportStatus === ReportStatus.PROCESSING && (
                            <LoopOutlined
                              sx={{
                                animation: "spin 2s linear infinite",
                                "@keyframes spin": {
                                  "0%": {
                                    transform: "rotate(360deg)",
                                  },
                                  "100%": {
                                    transform: "rotate(0deg)",
                                  },
                                },
                              }}
                            />
                          )}
                          {item.reportStatus === ReportStatus.SUCCESS_NO_OBLIGATIONS && (
                            <VisibilityOutlined sx={{ color: green[300] }} />
                          )}
                          {item.reportStatus === ReportStatus.SUCCESS_WITH_OBLIGATIONS && (
                            <VisibilityOutlined sx={{ color: deepOrange[200] }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={form.pageCount}
          onChange={form.onPageChanged}
          page={form.watch(nameof(defaultModel.currentPage))}
        />
      </>
    ) : (
      form.searched && <AlertExtended type="warning">Няма намерени резултати.</AlertExtended>
    );
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Заявени проверки на задължено лице</Typography>
      {renderForm()}
      {renderTable()}
    </Stack>
  );
}
